import { Box, Flex, Grid, Stack, Text } from "@chakra-ui/react";
import moment from "moment";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { type OptionDto } from "../../../autogen/bff-api";
import { isDisplayableDate } from "../../../common/formatting/displayDate";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { type ExtensionName, useExtensionName } from "./extensionName";
import { contractEnds, daysFromToday, extendsContract, inPeriodOrder } from "./extensionStatus";

interface Props {
  options: OptionDto[];
  startDate?: string | null;
  endDate?: string | null;
  today: Date;
}

export const ExtensionSummary = ({ options, endDate, today }: Omit<Props, "startDate">) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const { endsNow, endsWithAllExtensions, nextDeadline: deadline } = contractEnds(options, endDate);
  const daysLeft = deadline ? daysFromToday(deadline, today) : undefined;
  const nextDeadline = !deadline
    ? t("None")
    : daysLeft !== undefined && daysLeft < 0
      ? t("{{date}} (passed)", { date: displayDate(deadline) })
      : daysLeft === 0
        ? t("{{date}} (today)", { date: displayDate(deadline) })
        : t("{{date}} ({{count}} days left)", { date: displayDate(deadline), count: daysLeft });

  return (
    <Grid templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }} gap="3" data-testid="extension-summary">
      <SummaryFigure label={t("End date now")} value={endsNow ? displayDate(endsNow) : t("No end date")} />
      <SummaryFigure
        label={t("With all extensions")}
        value={endsWithAllExtensions ? displayDate(endsWithAllExtensions) : t("No end date")}
      />
      <SummaryFigure label={t("Next deadline")} value={nextDeadline} />
    </Grid>
  );
};

const SummaryFigure = ({ label, value }: { label: string; value: string }) => (
  <Box borderWidth="1px" borderColor="smBorder" rounded="md" px="3" py="2">
    <Text fontSize="sm" color="smMuted">
      {label}
    </Text>
    <Text fontWeight="semibold">{value}</Text>
  </Box>
);

interface Segment {
  key: string;
  name: string;
  start: string;
  end: string;
  look: "base" | "exercised" | "open" | "dropped";
  hasStart: boolean;
}

export const ExtensionTimeline = ({ options, startDate, endDate, today }: Props) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const nameOf = useExtensionName(options);
  const segments = timelineSegments(options, startDate, endDate, String(t("Base period")), nameOf);
  if (segments.length === 0) return null;

  const from = moment.min([moment(segments[0].start), moment(today)]);
  const to = moment.max([moment(segments[segments.length - 1].end), moment(today)]);
  const span = Math.max(to.diff(from, "days"), 1);
  const share = (date: string | Date) => (moment(date).diff(from, "days") / span) * 100;
  const todayLabel = t("Today {{date}}", { date: displayDate(today.toISOString()) });
  const todayIndex = segments.findIndex((segment) => !moment(segment.end).isBefore(today, "day"));
  const period = (segment: Segment) => {
    const start = segment.hasStart && isDisplayableDate(segment.start) ? displayDate(segment.start) : undefined;
    const end = isDisplayableDate(segment.end) ? displayDate(segment.end) : undefined;
    if (start && end) return `${start} – ${end}`;
    if (end) return segment.hasStart ? `${t("End date")}: ${end}` : `– ${end}`;
    return start ? `${t("Start date")}: ${start}` : "";
  };

  return (
    <Box data-testid="extension-timeline">
      <Box display={{ base: "none", md: "block" }} position="relative" pt="6">
        <Box
          position="absolute"
          top="0"
          bottom="0"
          left={`${share(today)}%`}
          borderLeftWidth="2px"
          borderColor="smText"
          zIndex={1}
          data-testid="timeline-today"
        >
          <Text
            fontSize="xs"
            color="smMuted"
            whiteSpace="nowrap"
            mt="-1"
            {...(share(today) > 50 ? { position: "absolute", top: "0", right: "0", mr: "1" } : { ml: "1" })}
            data-testid="timeline-today-label"
          >
            {todayLabel}
          </Text>
        </Box>
        <Flex position="relative" ml={`${share(segments[0].start)}%`} gap="1px">
          {segments.map((segment) => (
            <Box
              key={segment.key}
              flexGrow={Math.max(moment(segment.end).diff(segment.start, "days"), 1)}
              flexBasis="0"
              minW="0"
              h="3"
              rounded="sm"
              {...segmentLook[segment.look]}
              data-testid="timeline-bar"
            />
          ))}
        </Flex>
      </Box>
      <Stack spacing="2" mt={{ base: "0", md: "3" }} data-testid="timeline-legend">
        {segments.map((segment, index) => (
          <Fragment key={segment.key}>
            {index === todayIndex && <TodayRow label={todayLabel} />}
            <Flex gap="2" alignItems="flex-start">
              <Box w="3" h="3" mt="1" flexShrink={0} rounded="sm" {...segmentLook[segment.look]} />
              <Flex flexWrap="wrap" columnGap="2" minW="0" data-testid="timeline-row-text">
                <Text fontSize="sm" fontWeight="semibold">
                  {segment.name}
                </Text>
                <Text fontSize="sm" color="smMuted" whiteSpace="nowrap" data-testid="timeline-row-period">
                  {period(segment)}
                </Text>
              </Flex>
            </Flex>
          </Fragment>
        ))}
        {todayIndex === -1 && <TodayRow label={todayLabel} />}
      </Stack>
    </Box>
  );
};

const TodayRow = ({ label }: { label: string }) => (
  <Flex display={{ base: "flex", md: "none" }} alignItems="center" gap="2" data-testid="timeline-today-row">
    <Box w="3" flexShrink={0} borderTopWidth="2px" borderColor="smText" />
    <Text fontSize="xs" fontWeight="semibold" color="smMuted">
      {label}
    </Text>
  </Flex>
);

const segmentLook: Record<Segment["look"], Record<string, string>> = {
  base: { bg: "smPrimary" },
  exercised: { bg: "smPrimary" },
  open: { bg: "smPrimarySoft", borderWidth: "1px", borderStyle: "dashed", borderColor: "smPrimary" },
  dropped: { bg: "smBackgroundTertiary", borderWidth: "1px", borderColor: "smBorder" },
};

export const timelineSegments = (
  options: OptionDto[],
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  baseName: string,
  nameOf: ExtensionName
): Segment[] => {
  const dated = inPeriodOrder(options).filter((option) => option.startDate && option.endDate);
  const baseEnd = earlierOf(endDate, dayBefore(dated[0]?.startDate));
  if (!baseEnd) return [];
  const base: Segment = {
    key: "base",
    name: baseName,
    start: startDate ?? moment(baseEnd).subtract(1, "year").toISOString(),
    end: baseEnd,
    look: "base",
    hasStart: !!startDate,
  };
  return [
    base,
    ...dated.map((option) => ({
      key: option.id ?? option.name ?? "",
      name: nameOf(option),
      start: option.startDate as string,
      end: option.endDate as string,
      look: option.isExecuted ? "exercised" : extendsContract(option) ? "open" : "dropped",
      hasStart: true,
    })),
  ] as Segment[];
};

const dayBefore = (date: string | null | undefined): string | undefined =>
  date ? moment(date).subtract(1, "day").toISOString() : undefined;

const earlierOf = (a: string | null | undefined, b: string | undefined): string | null | undefined =>
  !a || !b ? b ?? a : moment(a).isBefore(b, "day") ? a : b;
